import { graphql, useStaticQuery } from "gatsby";
import React, { useCallback, useEffect, useRef, useState } from "react";

import ProgramList from "../../components/shared/programList";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { preFilterStandardPrograms } from "../../utils/base";
import { sortBy } from "../../utils/commonFunctions";
import { flattenMenu } from "../../utils/flattenMenu";

const ProgramsFiltered = ({
  locationData,
  showCommunityPrograms,
  customPrograms,
  sectionDefaultTitle,
  sectionTitleStyle,
  programListContainerClass,
  programListPaginationGradientDefaultClass,
  programListPaginationGradientClass,
  pageProgramFilters,
  programListFilterStrict = false,
  groupProgramSourceType,
  viewMoreText,
  sectionDefaultTitleStyle,
  programListDefaultContainerClass,
  programListConfig,
  programFilterMobileMenuCTAClass = ""
}) => {
  const { defaultPrograms, defaultSecondaryPrograms } = useStaticQuery(graphql`
    {
      defaultPrograms: allWpProgram(
        sort: { acfProgramsMeta: { programRating: DESC } }
      ) {
        nodes {
          ...programMiniDataFilterableFragment
        }
      }

      defaultSecondaryPrograms: _secondarySource {
        ...secondarySourceFragment
      }
    }
  `);

  const programsCollection =
    customPrograms?.length > 0 ? customPrograms : defaultPrograms;

  const prefilteredPrograms =
    programsCollection?.nodes?.map((prog) => prog) ?? programsCollection;

  const prefilteredSecondaryPrograms =
    defaultSecondaryPrograms?.programs?.nodes?.map((program) => ({
      ...program,
      uri: program?.uri?.replace("group-travel-single", "group-travel/schools")
    })) ?? [];

  //does a program filter exist?
  const programs = prefilteredPrograms?.filter((program) =>
    preFilterStandardPrograms(
      program,
      pageProgramFilters?.getFilterByProgramType,
      pageProgramFilters?.getFilterByProgramExtraTypes,
      pageProgramFilters?.getFilterByLocation,
      programListFilterStrict
    )
  );

  const secondaryPrograms = prefilteredSecondaryPrograms?.filter((program) =>
    preFilterStandardPrograms(
      program,
      pageProgramFilters?.getFilterByProgramType,
      pageProgramFilters?.getFilterByProgramExtraTypes,
      pageProgramFilters?.getFilterByLocation,
      programListFilterStrict
    )
  );

  const [sectionTitle, setSectionTitle] = useState("");
  const [visibleCnt, setVisibleCnt] = useState(8);
  const [showPagination, setShowPagination] = useState(true);
  const breakpoint = useBreakpoint();

  const filterBoxContainerRef = useRef(null);

  const [reformattedConservationPrograms, setReformattedConservationPrograms] =
    useState([]);
  const [reformattedCommunityPrograms, setReformattedCommunityPrograms] =
    useState([]);
  const [paginatedConservationPrograms, setPaginatedConservationPrograms] =
    useState([]);
  const [paginatedCommunityPrograms, setPaginatedCommunityPrograms] = useState(
    []
  );

  const [filterOptions, setFilterOptions] = useState([]);
  const [communityFilterOptions, setCommunityFilterOptions] = useState([]);
  const [mobileFilterOptions, setMobileFilterOptions] = useState([]);
  const [mobileCommunityFilterOptions, setMobileCommunityFilterOptions] =
    useState([]);
  const [currentFilteredItems, setCurrentFilteredItems] = useState([]);
  const [currentFilteredCommunityItems, setCurrentFilteredCommunityItems] =
    useState([]);

  const filterBoxHide =
    (pageProgramFilters?.getFilterByLocation?.length > 0 &&
      groupProgramSourceType) ??
    false;

  const initFormOptions = useCallback(async (formOption = "all") => {
    const programLocations = [];
    const programTypes = [];
    const communityProgramLocations = [];
    const communityProgramTypes = [];
    let taxonomyAdditionalTypes = [];

    let reformattedConservationPrograms = await programs?.map((program) => {
      const locations = flattenMenu(program?.geographical?.nodes, {
        idKey: "id",
        parentKey: "parentId",
        childrenKey: "children"
      });

      const desktopLocation = locations.flatMap((loc) => {
        let newlist = [];

        if (loc?.parentId !== null) {
          newlist.push(loc);
        }

        loc?.children.forEach((locChild) => {
          newlist.push(locChild);
          // newlist.push(locChild?.children);
        });

        return [...new Set(newlist.flat().filter((el) => el))];
      });

      desktopLocation.forEach((location) => {
        programLocations[location.slug] = {
          label: location.name,
          value: location.slug
        };
      });

      const types = program?.acfProgramsMeta?.programTypes?.flatMap((type) => {
        programTypes[type.slug] = {
          label: type.title,
          value: type.slug
        };

        return type.slug;
      });
      const extraTypes = program?.taxonomyAdditionalTypes?.nodes?.flatMap(
        (type) => {
          taxonomyAdditionalTypes[type.slug] = {
            label: type.title,
            value: type.slug
          };

          return type.slug;
        }
      );

      const groupTypes = program?.taxonomyGroupProgramTypes?.nodes.map(
        (type) => {
          programTypes[type.slug] = {
            label: type.name,

            value: type.slug
          };
          return type.slug;
        }
      );

      const { programHeaderSubsectionTitle } = program?.acfProgramsMeta ?? {};

      return {
        id: program.databaseId,
        title: program.title,
        subtitle: programHeaderSubsectionTitle || "",
        content: program.content,
        link: program.uri?.replace(
          "group-travel-single",
          "group-travel/schools"
        ),
        image: program?.featuredImage?.node?.localFile?.extension
          ? program?.featuredImage?.node?.localFile
          : null,
        sourceUrl: program?.featuredImage?.node?.sourceUrl,
        location: desktopLocation.flatMap((loc) => loc.slug) ?? [],
        types: types ?? groupTypes ?? [],
        extraTypes: extraTypes ?? []
      };
    });

    let reformattedCommunityPrograms = await secondaryPrograms?.map(
      (program) => {
        const locations = flattenMenu(program?.geographical?.nodes, {
          idKey: "id",
          parentKey: "parentId",
          childrenKey: "children"
        });

        const desktopLocation = locations.flatMap((loc) => {
          let newlist = [];

          if (loc?.parentId !== null) {
            newlist.push(loc);
          }

          loc?.children.forEach((locChild) => {
            newlist.push(locChild);
            // newlist.push(locChild?.children);
          });

          return [...new Set(newlist.flat().filter((el) => el))];
        });

        desktopLocation.forEach((location) => {
          communityProgramLocations[location.slug] = {
            label: location.name,
            value: location.slug
          };
        });

        const types = program?.acfProgramsMeta?.programTypes.flatMap((type) => {
          communityProgramTypes[type.slug] = {
            label: type.title,
            value: type.slug
          };

          return type.slug;
        });

        const extraTypes = program?.taxonomyAdditionalTypes?.nodes?.flatMap(
          (type) => {
            taxonomyAdditionalTypes[type.slug] = {
              label: type.title,
              value: type.slug
            };

            return type.slug;
          }
        );

        const groupTypes = program?.taxonomyGroupProgramTypes?.nodes.map(
          (type) => {
            communityProgramTypes[type.slug] = {
              label: type.name,
              value: type.slug
            };
            return type.slug;
          }
        );

        const { programHeaderSubsectionTitle } = program?.acfProgramsMeta ?? {};

        return {
          id: program.databaseId,
          title: program.title,
          subtitle: programHeaderSubsectionTitle || "",
          content: program.content,
          link: program?.uri?.replace(
            "group-travel-single",
            "group-travel/schools"
          ),
          image: program?.featuredImage?.node?.localFile?.extension
            ? program?.featuredImage?.node?.localFile
            : null,
          sourceUrl: program?.featuredImage?.node?.sourceUrl,
          location: desktopLocation.flatMap((loc) => loc.slug) ?? [],
          types: types ?? groupTypes ?? [],
          extraTypes: extraTypes ?? []
        };
      }
    );

    setFilterOptions([
      {
        name: "programType",
        label: {
          prefix: "Select",
          body: "Program Types"
        },
        defaultValue:
          Object.values(programTypes)
            .filter((type) =>
              pageProgramFilters?.getFilterByProgramType.includes(type.value)
            )
            .shift() || null,
        list: Object.values(programTypes).sort(sortBy("value"))
      },
      {
        name: "programLocation",
        label: {
          prefix: "Choose",
          body: "Locations"
        },
        defaultValue:
          Object.values(programLocations)
            .filter((loc) =>
              pageProgramFilters?.getFilterByLocation.includes(loc.value)
            )
            .shift() || null,
        list: Object.values(programLocations).sort(sortBy("value"))
      }
    ]);

    setCommunityFilterOptions([
      {
        name: "programType",
        label: {
          prefix: "Select",
          body: "Program Types"
        },
        defaultValue:
          Object.values(communityProgramTypes)
            .filter((type) =>
              pageProgramFilters?.getFilterByProgramType.includes(type.value)
            )
            .shift() || null,
        list: Object.values(communityProgramTypes).sort(sortBy("value"))
      },
      {
        name: "programLocation",
        label: {
          prefix: "Choose",
          body: "Locations"
        },
        defaultValue:
          Object.values(communityProgramLocations)
            .filter((loc) =>
              pageProgramFilters?.getFilterByLocation.includes(loc.value)
            )
            .shift() || null,
        list: Object.values(communityProgramLocations).sort(sortBy("value"))
      }
    ]);

    if (formOption === "conservation" || formOption === "all") {
      setCurrentFilteredItems(reformattedConservationPrograms);
      setPaginatedConservationPrograms(
        reformattedConservationPrograms?.slice(
          0,
          breakpoint === "xs"
            ? reformattedConservationPrograms?.length
            : visibleCnt
        )
      );
      setReformattedConservationPrograms(reformattedConservationPrograms);
    }
    if (formOption === "community" || formOption === "all") {
      setCurrentFilteredCommunityItems(reformattedCommunityPrograms);
      setPaginatedCommunityPrograms(
        reformattedCommunityPrograms?.slice(
          0,
          breakpoint === "xs"
            ? reformattedCommunityPrograms?.length
            : visibleCnt
        )
      );

      setReformattedCommunityPrograms(reformattedCommunityPrograms);
    }
  }, []);

  const handleProgramFilter = useCallback(
    (objValues) => {
      const programTypeValue = objValues.programType
        ? objValues.programType.value
        : "";
      const locationValue = objValues.programLocation
        ? objValues.programLocation.value
        : "";

      if (objValues === "clear") {
        initFormOptions("conservation");
      } else if (programTypeValue || locationValue) {
        const filterConditions = {
          byLocation: (item) => item.location.includes(locationValue ?? ""),
          byProgramType: (item) => item.types.includes(programTypeValue ?? "")
        };

        let selected = [];

        if (locationValue) {
          selected = [...selected, filterConditions.byLocation];
        }
        if (programTypeValue) {
          selected = [...selected, filterConditions.byProgramType];
        }

        const result = reformattedConservationPrograms.filter((program) =>
          selected.every((f) => f(program))
        );
        setCurrentFilteredItems(result);

        setPaginatedConservationPrograms(result.slice(0, 4));
      }
    },
    [reformattedConservationPrograms, initFormOptions]
  );

  const handleCommunityProgramFilter = useCallback(
    (objValues) => {
      const programTypeValue = objValues.programType
        ? objValues.programType.value
        : "";
      const locationValue = objValues.programLocation
        ? objValues.programLocation.value
        : "";

      if (objValues === "clear") {
        initFormOptions("community");
      } else if (programTypeValue || locationValue) {
        const filterConditions = {
          byLocation: (item) => item.location.includes(locationValue ?? ""),
          byProgramType: (item) => item.types.includes(programTypeValue ?? "")
        };

        let selected = [];

        if (locationValue) {
          selected = [...selected, filterConditions.byLocation];
        }
        if (programTypeValue) {
          selected = [...selected, filterConditions.byProgramType];
        }

        const result = reformattedCommunityPrograms.filter((program) =>
          selected.every((f) => f(program))
        );
        setCurrentFilteredCommunityItems(result);

        setPaginatedCommunityPrograms(result.slice(0, 4));
      }
    },
    [reformattedCommunityPrograms, initFormOptions]
  );

  const handlePagination = useCallback(() => {
    setVisibleCnt((prevState) => prevState + 4);
  }, []);

  useEffect(() => {
    if (
      showCommunityPrograms === true &&
      currentFilteredCommunityItems?.length > visibleCnt &&
      currentFilteredItems?.length > visibleCnt
    ) {
      setShowPagination(true);
    } else if (currentFilteredItems?.length > visibleCnt) {
      setShowPagination(true);
    } else if (
      showCommunityPrograms === true &&
      currentFilteredCommunityItems?.length > visibleCnt
    ) {
      setShowPagination(true);
    } else {
      setShowPagination(false);
    }

    // console.debug("currentFilteredItems", {
    //   currentFilteredCommunityItems,
    //   currentFilteredItems,
    //   visibleCnt,
    //   showCommunityPrograms
    // });

    setPaginatedConservationPrograms(
      currentFilteredItems?.slice(
        0,
        breakpoint === "xs" ? currentFilteredItems?.length : visibleCnt
      )
    );

    setPaginatedCommunityPrograms(
      currentFilteredCommunityItems?.slice(
        0,
        breakpoint === "xs" ? currentFilteredCommunityItems?.length : visibleCnt
      )
    );
  }, [
    currentFilteredItems,
    currentFilteredCommunityItems,
    visibleCnt,
    showCommunityPrograms
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const filterContainerPosition =
        filterBoxContainerRef.current?.getBoundingClientRect().top;
      if (filterBoxContainerRef.current != null) {
        if (filterContainerPosition < 300) {
          filterBoxContainerRef.current.children[0].style.position = "fixed";
          filterBoxContainerRef.current.children[0].style.top = "64px";
          filterBoxContainerRef.current.children[0].style.right = 0;
        } else {
          filterBoxContainerRef.current.children[0].style.position = "absolute";
          filterBoxContainerRef.current.children[0].style.top = "-240px";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const locationTitles =
      locationData?.flatMap((location) => location.name) && !filterBoxHide
        ? locationData?.flatMap((location) => location.name)
        : [];

    const sectTitlePrefix =
      locationTitles.length > 0 && !filterBoxHide
        ? "Programs in"
        : sectionDefaultTitle;

    setSectionTitle(`${sectTitlePrefix} ${locationTitles.join(", ")}`);
  }, [filterBoxHide, locationData, sectionDefaultTitle]);

  useEffect(() => {
    if (breakpoint === "xs") {
      setVisibleCnt(-1);
      setShowPagination(false);
    }
  }, [breakpoint]);

  useEffect(() => {
    const mobilemenu = filterOptions.map((menu) => {
      return {
        name: menu.name,
        label: menu.label.body,
        value: menu.value,
        list: menu.list?.map((item) => ({ ...item, filter_name: menu.name }))
      };
    });

    setMobileFilterOptions(mobilemenu);
  }, [filterOptions]);

  useEffect(() => {
    const mobileCommunityMenu = communityFilterOptions.map((menu) => {
      return {
        name: menu.name,
        label: menu.label.body,
        value: menu.value,
        list: menu.list?.map((item) => ({ ...item, filter_name: menu.name }))
      };
    });

    setMobileCommunityFilterOptions(mobileCommunityMenu);
  }, [communityFilterOptions]);

  useEffect(() => {
    initFormOptions();
  }, [initFormOptions]);

  // console.debug("location", locationData);
  // console.debug("genProgramExtraTypes", genProgramExtraTypes);
  // console.debug("pageProgramFilters", {
  //   pageProgramFilters,
  //   groupProgramSourceType,
  //   locationData,
  //   showCommunityPrograms,
  //   customPrograms,
  //   sectionDefaultTitle,
  // });
  // console.debug("reformattedCommunityPrograms", paginatedConservationPrograms);
  // console.debug("programListConfig", programListConfig);

  return (
    <>
      {(reformattedConservationPrograms?.length > 0 ||
        reformattedCommunityPrograms?.length > 0) && (
        <ProgramList
          title={sectionTitle}
          titleStyle={sectionTitleStyle}
          titleDefaultStyle={sectionDefaultTitleStyle}
          sectionContainerDefaultClass={programListDefaultContainerClass}
          sectionContainerClass={programListContainerClass}
          sectionPaginationGradientClass={programListPaginationGradientClass}
          sectionPaginationGradientDefaultClass={
            programListPaginationGradientDefaultClass
          }
          conservationPrograms={paginatedConservationPrograms}
          communityPrograms={paginatedCommunityPrograms}
          sortByType={showCommunityPrograms}
          filterCallback={handleProgramFilter}
          filterOptions={filterOptions}
          filterCommunityCallback={handleCommunityProgramFilter}
          filterCommunityOptions={communityFilterOptions}
          filterBoxHide={filterBoxHide}
          paginationCallback={handlePagination}
          pagination={showPagination}
          mobileFilterOptions={mobileFilterOptions}
          mobileCommunityFilterOptions={mobileCommunityFilterOptions}
          viewMoreText={viewMoreText}
          {...programListConfig}
        />
      )}
    </>
  );
};

export default ProgramsFiltered;

ProgramsFiltered.defaultProps = {
  sectionTitleStyle: "md:bg-gray-tertiary planet:md:bg-gray-dark",
  showCommunityPrograms: false,
  groupProgramSourceType: false,
  sectionDefaultTitle: "Our programs",
  customPrograms: [],
  programListConfig: [],
  pageProgramFilters: {
    getFilterByProgramType: [],
    getFilterByProgramExtraTypes: [],
    getFilterByLocation: []
  }
};
